import { type Module } from "vuex";
import type { AxiosError } from "axios";
import type { AgentVoice, DefaultApi } from "@/api";
import {
  Subscription,
  SUBSCRIPTION_DEFAULT_AGENT_NAME,
} from "@/models/subscription";
import type { State as RootState } from "@/stores";
export interface State {
  subscription?: Subscription;
  loaded: boolean;
}
const pendingSubscriptions = new WeakMap<State, Promise<Subscription | null>>();

export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    subscription: undefined,
    loaded: false,
  },
  getters: {
    agentName: (state) =>
      state.subscription?.agent_name ?? SUBSCRIPTION_DEFAULT_AGENT_NAME,
    canViewRanking: (state) => state.subscription?.canViewRanking ?? false,
    canInviteAllCandidates: (state) =>
      state.subscription?.canInviteAllCandidates ?? false,
    canInviteTop10Candidates: (state) =>
      state.subscription?.canInviteTop10Candidates ?? false,
    isUpgradable: (state) =>
      state.subscription === undefined || state.subscription?.isUpgradable,
  },
  actions: {
    async getSubscription({ state, commit }) {
      if (state.loaded) return state.subscription ?? null;
      const pending = pendingSubscriptions.get(state);
      if (pending) return pending;

      const request = Promise.resolve().then(async () => {
        try {
          const api: DefaultApi = await this.dispatch(
            "getAuthorizedApi",
            null,
            {
              root: true,
            },
          );
          const response = await api.getSubscription();
          const subscription = new Subscription(response.data);
          if (pendingSubscriptions.get(state) === request)
            commit("setSubscription", subscription);
          return subscription;
        } catch (error) {
          if ((error as AxiosError).response?.status === 404) {
            if (pendingSubscriptions.get(state) === request)
              commit("setSubscription", undefined);
            return null;
          }
          throw error;
        }
      });
      pendingSubscriptions.set(state, request);
      try {
        return await request;
      } finally {
        if (pendingSubscriptions.get(state) === request)
          pendingSubscriptions.delete(state);
      }
    },
    async postSubscriptionAgentVoice(
      { commit, dispatch },
      agentVoiceId: AgentVoice,
    ) {
      const api: DefaultApi = await dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postSubscriptionAgentVoice(agentVoiceId);
      commit("invalidateSubscription");
      return response;
    },
  },
  mutations: {
    setSubscription(state, subscription?: Subscription) {
      state.subscription = subscription;
      state.loaded = true;
    },
    invalidateSubscription(state) {
      state.loaded = false;
      pendingSubscriptions.delete(state);
    },
  },
};
